import { useCallback, useEffect, useState } from "react";
import { getManufacturingEntitlement } from "@/lib/manufacturingClient";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";

/**
 * Shared gate hook for manufacturing-bucket panels. Each panel calls this
 * to render the same loading / error / disabled-entitlement banner before
 * its own mode-specific data fetches. Refetches automatically when the
 * scoped org switches (impersonation / org switcher), so the gate banner
 * flips correctly without a full page refresh.
 */
export function useManufacturingEntitlementGate() {
  const scopedOrganizationId = useScopedOrganizationId();
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (opts?: { refresh?: boolean }) => {
    if (opts?.refresh) setWorking(true);
    else setLoading(true);
    setError(null);

    const entitlement = await getManufacturingEntitlement();
    if (!entitlement.ok) {
      setEnabled(false);
      setError(entitlement.error);
      setLoading(false);
      setWorking(false);
      return;
    }
    setEnabled(entitlement.data.enabled);
    setLoading(false);
    setWorking(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load, scopedOrganizationId]);

  return {
    loading,
    working,
    enabled,
    error,
    scopedOrganizationId,
    refresh: () => void load({ refresh: true }),
  };
}
